import {
  Directive,
  ElementRef,
  Input,
  HostListener,
  OnInit,
  OnDestroy
} from '@angular/core'
import { Observable, Subscription } from 'rxjs/Rx'
import { DomUtils } from '@mprep/shared/lib/helpers'

import { Window, Utils } from './../'

@Directive({
  selector: '[mpIframeResize]'
})
export class IframeResizeDirective implements OnInit, OnDestroy {

  private _domIframeElement: HTMLIFrameElement
  private _sizeStrategyInstance: IframeSizeStrategy
  private _sizeChangeSubscription: Subscription
  private _strategyInput: string
  private _cachedBackgroundColor: string
  private _cachedBackgroundImage: string
  private static ACTIVE_BACKGROUND_COLOR: string = '#FFFFFF'
  private _window: any // HTML Window object

  /**
   * Input specifying the strategy to be used for determining the size of the iframe
   * Valid values are either 'document' or 'window'
   */
  @Input('mpIframeResize') public set mpIframeResize(value: string) { this._strategyInput = value}

  public constructor(el: ElementRef, private _domUtils: DomUtils) {
    this._domIframeElement = el.nativeElement
    this._strategyInput = ''
    this._window = (new Window()).getInstance()
  }

  @HostListener('load') public onload() {
    this.resize()
  }

  public ngOnInit() {
    this._domIframeElement.style.visibility = 'hidden'
    this.replaceBackground()
  }

  public ngOnDestroy() {
    this.unwatchSizeChanges()
    this.restoreBackground()
  }

  private resize(): void {
    if (this._domIframeElement.src === '') { return } // MP-TODO: We should figure out, why this happens

    let resizeSynchronous = () => {
      if (!Utils.propertyPresent(this, '_domIframeElement', 'contentWindow')) { return }
      this._sizeStrategyInstance = this.getSizeStrategy(this._strategyInput, this._domIframeElement)
      this._sizeStrategyInstance.resize()
      this.watchSizeChanges(this._sizeStrategyInstance)
      try {
        this._domIframeElement.contentWindow.addEventListener('unload', this.unwatchSizeChanges.bind(this))
      } catch (e) {}
      this._domIframeElement.style.visibility = 'visible'
    }
    setTimeout(resizeSynchronous, 1)
  }

  private getSizeStrategy(strategyName: string, domIframeElement: HTMLIFrameElement): IframeSizeStrategy {
    let strategyNameIsWindow = strategyName.toLowerCase() === 'window'
    let documentSizeStrategyIsApplicable = this.isDocumentSizeStrategyApplicable(domIframeElement)

    let SizeStrategyClass: { new(el, window): IframeSizeStrategy } = (
      strategyNameIsWindow || !documentSizeStrategyIsApplicable ? WindowSizeStrategy : DocumentSizeStrategy
    )

    return new SizeStrategyClass(domIframeElement, this._window)
  }

  private isDocumentSizeStrategyApplicable(domIframeElement: HTMLIFrameElement): boolean {
    let iframeContentAccessible = false
    const currentDomain = this._window.document.domain
    try {
      iframeContentAccessible = (currentDomain === domIframeElement.contentWindow.document.domain)
    } catch (ignoredError) {
      // We want to ignore any error here, as this determines for us whether we
      // can tap into the iframe. Some browsers will for instance return
      // `undefined` for `contentWindow.document` if we don't have access, while
      // others may throw an exception (DOMException).
      // In addition to that: We know this can fail, therefore we have a fallback
      // strategy in place (WindowSizeStrategy)
    }
    return iframeContentAccessible
  }

  private watchSizeChanges(sizeStrategyInstance: IframeSizeStrategy): void {
    this._sizeStrategyInstance = this._sizeStrategyInstance || sizeStrategyInstance
    if (typeof this._sizeChangeSubscription === 'undefined') {
      this._sizeChangeSubscription = sizeStrategyInstance.onSizeChanged()
        .subscribe((newSize) => {
          sizeStrategyInstance.resize()
        })
    }
  }

  private unwatchSizeChanges(): void {
    if (typeof this._sizeChangeSubscription !== 'undefined' ) {
      this._sizeChangeSubscription.unsubscribe()
      this._sizeChangeSubscription = undefined
      this._sizeStrategyInstance = undefined
    }
  }

  private replaceBackground() {
    this._cachedBackgroundColor = this._domUtils.getBodyBackgroundColor()
    this._cachedBackgroundImage = this._domUtils.getBodyBackgroundImage()
    this._domUtils.setBodyBackgroundColor(IframeResizeDirective.ACTIVE_BACKGROUND_COLOR)
    this._domUtils.removeBodyBackgroundImage()
  }

  private restoreBackground() {
    this._domUtils.setBodyBackgroundColor(this._cachedBackgroundColor)
    this._domUtils.setBodyBackgroundImage(this._cachedBackgroundImage)
  }
}

interface CssDimension {
  value: string,
  unit: string
}

function CssDimensionFromString(cssStyleString: string): CssDimension {
  let [value, unit] = (typeof cssStyleString === 'string' && cssStyleString.length > 0)
    ? [cssStyleString.match(/(\d+(?:\.\d+)?)([a-z%]+)/i)[1], cssStyleString.match(/(\d+(?:\.\d+)?)([a-z%]+)/i)[2]]
    : ['0', 'px']

  return {
    value: value,
    unit: unit
  }
}

/**
 * An abstract class for different strategies to be used to determine the size of an HTMLIFrameElement.
 * The abstract class implements functionality shared by subclases so that only few things need to be implemented.
 */
abstract class IframeSizeStrategy {

  private _sizeChangeObserver: Observable<{width: CssDimension, height: CssDimension}>

  constructor(protected _domIframeElement: HTMLIFrameElement, protected _window: any) {}

  public abstract width(): CssDimension;
  public abstract height(): CssDimension;

  public onSizeChanged(): Observable<any> {
    if (typeof this._sizeChangeObserver === 'undefined') {
      this._sizeChangeObserver = this.sizeChangeObserverTemplate()
    }
    return this._sizeChangeObserver.catch((err: any, caught: any) => err)
  }

  public resize() {
    let [previousWidth, previousHeight] = [
      CssDimensionFromString(this._domIframeElement.style.width),
      CssDimensionFromString(this._domIframeElement.style.width)
    ]

    try {
      this.setSize(this.width(), this.height())
    } catch (anything) {
      this.setSize(previousWidth, previousHeight)
    }
  }

  /**
   * Default implementation of a template method that is meant to be overriden by child classes
   */
  protected sizeChangeObserverTemplate(): Observable<{width: CssDimension, height: CssDimension}> {
    return <Observable<{width: CssDimension, height: CssDimension}>> Observable.never()
  }

  protected resetHeight(heightInPixels: number = 1): { width: CssDimension, height: CssDimension } {
    let previousState = {
      width: CssDimensionFromString(this._domIframeElement.style.width),
      height: CssDimensionFromString(this._domIframeElement.style.height)
    }
    this.setSize({value: '100', unit: '%'}, { value: heightInPixels.toString(), unit: 'px'})

    return previousState
  }

  protected restoreSize(sizeSpec: { width: CssDimension, height: CssDimension }) {
    this.setSize(sizeSpec.width, sizeSpec.height)
  }

  protected setSize(width: CssDimension, height: CssDimension) {
    if ('width' in this._domIframeElement) { this._domIframeElement.width = '' }
    if ('height' in this._domIframeElement) {this._domIframeElement.height = '' }

    this._domIframeElement.style.width = width.value + width.unit
    this._domIframeElement.style.height = height.value + height.unit
  }

}

/**
 * This IframeSizeStrategy returns the size based on available viewport inside a window that contains an iframe.
 * The available viewport is defined as the area between the top of the iframe element and the bottom-most visible pixel.
 *
 * To determine size changes it re-calculates the size whenever the window size changes.
 */
class WindowSizeStrategy extends IframeSizeStrategy {

  /**
   * @overriden
   */
  public width(): CssDimension {
    return {value: '1', unit: 'px'}
  }

  /**
   * @overriden
   */
  public height(): CssDimension {
    let topOfIframe = this._domIframeElement.getBoundingClientRect().top
    let availableHeight = this._window.innerHeight
    return {value: (Math.round(availableHeight - topOfIframe)).toString(), unit: 'px'}
  }

  public constructor(protected _domIframeElement: HTMLIFrameElement, protected _window: any) {
    super(_domIframeElement, _window)
    // This solves an issue in IOS where the iframe was not properly using 100%
    this._domIframeElement.style.minWidth = '100%'
  }

  /**
   * @overriden
   */
  protected sizeChangeObserverTemplate(): Observable<{width: CssDimension, height: CssDimension}> {
    let observedWindow = this._window
    return Observable.create((observer) => {
      let eventHandler = (event) => observer.next({height: this.height(), width: this.width()})
      observedWindow.addEventListener('resize', eventHandler)
      return () => observedWindow.removeEventListener('resize', eventHandler)
    }).share()
  }
}

/**
 * This IframeSizeStrategy returns the size based on the dimensions of the document loaded inside an iframe,
 * which requires the iframe to be in the same origin as the document it's loaded in (i.e. this strategy can only be applied
 * in same origin scenarios).
 *
 * To determine size changes within the document, it listens to all DOM modifications inside the document an will emit
 * the document's new dimensions if applicable. In addition to that it listens to any global window size changes in order to
 * determine if the size has changed (i.e. if the iframe gets wider, the height of the document will most likely be shorter)
 *
 * Using this strategy essentially allows the browser to display the whole iframe without any scrollbars.
 *
 * @see {WindowSizeStrategy}
 */
class DocumentSizeStrategy extends IframeSizeStrategy {

  private _windowSizeStrategy: WindowSizeStrategy
  private _windowSizeStrategyObservable: Observable<{width: CssDimension, height: CssDimension}>
  private _changeObservableController: any

  private _windowInstance: any

  private _iframeHtmlElement: HTMLHtmlElement
  private _iframeBodyElement: HTMLBodyElement

  constructor(domElement: HTMLIFrameElement, windowInstance: any) {
    super(domElement, windowInstance)

    this._windowSizeStrategy = new WindowSizeStrategy(domElement, windowInstance)
    this._windowSizeStrategyObservable = this._windowSizeStrategy.onSizeChanged()
    this._windowInstance = windowInstance

    let iframeDocument = this._domIframeElement.contentWindow.document
    this._iframeHtmlElement = <HTMLHtmlElement> iframeDocument.documentElement
    this._iframeBodyElement = <HTMLBodyElement> iframeDocument.body

    this._changeObservableController = (value: any, index: number, observable: Observable<any>) =>  {
      return this._changeObservableController.active
    }
    this._changeObservableController.active = true
    this._changeObservableController.setActive = (isActive: boolean) => {
      this._changeObservableController.active = isActive
    }
  }

  /**
   * @overriden
   */
  public width(): CssDimension {
    return {value: '100', unit: '%'}
  }

  /**
   * Returns the height of the iframe's content in its current state (or the current height of the iframe).
   *
   * It does not always return the true content height, e.g., if the iframe's height is larger than the content, it'll
   * return the iframe's height.
   * Conversely, this means, the true height of the content is only returned if the iframe's height is smaller than the
   * content height (and usually scrollbars would be displayed).
   *
   * @overriden
   */
  public height(): CssDimension {
    const height = Math.max(
      this._iframeBodyElement.scrollHeight,
      this._iframeBodyElement.offsetHeight,
      this._iframeHtmlElement.scrollHeight,
      this._iframeHtmlElement.offsetHeight,
      this._iframeHtmlElement.clientHeight
    )

    // The assumption is, that the iFrame we load is not empty, i.e. its height should always be 1+ pixels.
    // Therefore, if the height is <= 1 px, we assume the height could not be determined.
    // In this case, we'll just use all the available space in the window and cross our fingers that everything fits nicely.
    const successfullyDeterminedHeight = height > 1
    return successfullyDeterminedHeight ? {value: height.toString(), unit: 'px'} : this._windowSizeStrategy.height()
  }

  /**
   * @overriden
   */
  public resize(): void {
    let previousVerticalScrollPosition = this._windowInstance.pageYOffset
    this._iframeHtmlElement.style.overflow = 'hidden'

    // This actual resizing process runs in a `setTimeout` function to determine the correct size for animations
    // (that are increasing the height of the content) and also it fixes loading PDFs in an Iframe Chrome.
    // TODO [GitHub Issue]: We might want to find a more robust, non-time-dependent method to achieve these results
    setTimeout(() => {
      // Pause the change observer so that we don't catch other 'size changed' events during resizing
      this._changeObservableController.setActive(false)

      // Reset height to 1px so that the content's height can be detected properly
      // Otherwise, if the content shrunk, it's always returning the current height
      let previousSize = this.resetHeight(1)
      super.resize()

      if (this._domIframeElement.style.height === '1px') { this.restoreSize(previousSize) }
      this._changeObservableController.setActive(true)

      if (previousVerticalScrollPosition !== this._windowInstance.pageYOffset) {
        this._windowInstance.scrollTo(this._windowInstance.pageXOffset, previousVerticalScrollPosition)
      }
      this._domIframeElement.style.visibility = 'visible'
    }, 50)
  }

  /**
   * @overriden
   */
  protected sizeChangeObserverTemplate(): Observable<{width: CssDimension, height: CssDimension}> {
    let result: Observable<{width: CssDimension, height: CssDimension}>
    try {
      let [previousHeight, previousWidth] = [this.height(), this.width()]
      result = Observable.create((observer) => {
        let emitNewDimensions = () => {
          // TODO [GitHub Issue]: The height value may be erroneous if the content height < iframe height
          let [height, width] = [this.height(), this.width()]
          if (!(height.value === previousHeight.value && height.unit === previousHeight.unit
              && width.value === previousWidth.value && width.unit === previousWidth.unit)) {
            observer.next({height: height, width: width})
          }
          previousHeight = height
          previousWidth = width
        }
        let domChangeObserver = new MutationObserver(emitNewDimensions)
        domChangeObserver
          .observe(this._iframeHtmlElement, {childList: true, attributes: true, characterData: true, subtree: true })

        return () => domChangeObserver.disconnect() // disconnects MutationObserver on unsubscribe
      }).share()
    } catch (securityRelatedException) {
      throw securityRelatedException
    }

    return result.merge(this._windowSizeStrategyObservable
      .map((changedDimensions) => { return {height: this.height(), width: this.width()} }))
      .filter(this._changeObservableController)
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/directives/iframe-resize.directive.ts